import React, { useState, useEffect } from "react";
import axios from "axios";
import "./_RepairManManager.scss";
import swal from "sweetalert";
import { getApiEndpoint, getImageUrl } from "../../../../utils/apiConfig";
import axiosWithAuth from "../../../../utils/axiosWithAuth";

const RepairManManager = () => {
  const [repairMans, setRepairMans] = useState({
    name: "",
    surname: "",
    phoneNumber: "",
    technicalArea: { id: null },
    image: {
      id: null,
      status: true,
    },
    status: true,
  });
  const [repairMan, setRepairMan] = useState([]);
  const [technicalAreas, setTechnicalAreas] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const authToken = localStorage.getItem("authToken");

  useEffect(() => {
    fetchTechnicalAreas();
    fetchRepairMan();
  }, []);

  const fetchTechnicalAreas = () => {
    axiosWithAuth()
      .get("technical-areas/admin")
      .then((response) => {
        setTechnicalAreas(response.data);
      })
      .catch((error) =>
        console.error("Failed to fetch technical areas:", error)
      );
  };

  const fetchRepairMan = () => {
    axiosWithAuth()
      .get("repairman")
      .then((response) => {
        setRepairMan(response.data);
      })
      .catch((error) => console.error("Failed to fetch repairMan:", error));
  };

  const uploadImage = (file) => {
    // Check if the file is too large (> 2MB)
    if (file.size > 2 * 1024 * 1024) {
      return compressAndUploadImage(file);
    }
    
    const formData = new FormData();
    formData.append("file", file);

    return axiosWithAuth().post(
      "mono-files/upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
  };

  // Add image compression function
  const compressAndUploadImage = (file) => {
    return new Promise((resolve, reject) => {
      console.log("Compressing image before upload...");
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Calculate new dimensions - keeping aspect ratio but reducing size
          let width = img.width;
          let height = img.height;
          
          // Target max dimensions
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with reduced quality
          canvas.toBlob((blob) => {
            // Create a new file from the blob
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            
            // Now upload the compressed file
            const formData = new FormData();
            formData.append("file", compressedFile);
            
            axiosWithAuth().post(
              "mono-files/upload",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                }
              }
            )
            .then(resolve)
            .catch(reject);
          }, 'image/jpeg', 0.7); // 0.7 quality - adjust as needed
        };
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = (event) => {
    const fileInput = event.target;
    if (fileInput && fileInput.files && fileInput.files[0]) {
      const file = fileInput.files[0];
      uploadImage(file)
        .then((response) => {
          const imageId = response.data.id;
          setRepairMans((prevRepairMan) => ({
            ...prevRepairMan,
            image: { ...prevRepairMan.image, id: imageId },
          }));
        })
        .catch((error) => {
          console.log("Failed to upload image:", error);
          swal({
            title: "Error",
            text: "Failed to upload image. Please try a smaller image.",
            icon: "error",
          });
        });
    } else {
      console.log("No file selected");
    }
  };

  const handleTechnicalAreaChange = (event) => {
    const selectedId = event.target.value;
    setRepairMans((prevRepairMan) => ({
      ...prevRepairMan,
      technicalArea: { id: selectedId },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Create a copy of the repairMans data
    const repairMansData = { ...repairMans };
    
    // Convert objects with id properties to just the ID strings
    // This fixes the "Cast to ObjectId failed" error
    if (repairMansData.technicalArea && repairMansData.technicalArea.id) {
      repairMansData.technicalArea = repairMansData.technicalArea.id;
    }
    
    if (repairMansData.image && repairMansData.image.id) {
      repairMansData.image = repairMansData.image.id;
    }
    
    console.log("Submitting repairman data:", repairMansData);

    const url = isEditing
      ? `repairman/${editId}`
      : "repairman";

    const request = isEditing
      ? axiosWithAuth().put(url, repairMansData)
      : axiosWithAuth().post(url, repairMansData);

    request
      .then(() => {
        setIsEditing(false);
        setEditId(null);
        setRepairMans({
          name: "",
          surname: "",
          phoneNumber: "",
          image: { id: null, status: true },
          technicalArea: { id: null },
          status: true,
        });
        fetchRepairMan();
        swal({
          title: "",
          text: "Repairman has been added",
          icon: "success",
          timer: 1500,
        });
      })
      .catch((error) => {
        console.error("Failed to submit repairMans:", error);
        // Show detailed error message if available
        const errorMessage = error.response?.data?.message || "Repairman has not been added";
        const errorDetails = error.response?.data?.details || "";
        swal({
          title: "",
          text: `${errorMessage}\n${errorDetails}`,
          icon: "error",
          timer: 3000,
        });
      });
  };
  const handleEdit = (type) => {
    // Create a cleaned up copy of the repairman data that matches our form state structure
    const repairmanForEdit = {
      ...type,
      id: type.id || type._id, // Handle both id formats
      technicalArea: {
        id: type.technicalArea?._id || type.technicalArea?.id || null
      },
      image: type.image || {
        id: null,
        status: true
      }
    };
    
    setRepairMans(repairmanForEdit);
    setEditId(type._id || type.id);
    setIsEditing(true);
  };

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "Once deleted, you will not be able to recover this repairman!",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    })
    .then((willDelete) => {
      if (willDelete) {
        axiosWithAuth()
          .delete(`repairman/${id}`)
          .then(() => {
            fetchRepairMan();
            swal("Repairman has been deleted!", {
              icon: "success",
              timer: 1500
            });
          })
          .catch((error) => {
            console.log(error);
            swal("Error deleting repairman!", {
              icon: "error",
              timer: 1500
            });
          });
      }
    });
  };
  return (
    <div className="repairMans-manager">
      <h3 className="text-center">RepairMan Manager</h3>
      <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Name</label>
            <input
              className="form-control"
              type="text"
              value={repairMans.name}
              onChange={(e) =>
                setRepairMans({ ...repairMans, name: e.target.value })
              }
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Surname</label>
            <input
              className="form-control"
              type="text"
              value={repairMans.surname}
              onChange={(e) =>
                setRepairMans({ ...repairMans, surname: e.target.value })
              }
              required
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Phone Number</label>
            <input
              className="form-control"
              type="text"
              value={repairMans.phoneNumber}
              onChange={(e) =>
                setRepairMans({ ...repairMans, phoneNumber: e.target.value })
              }
              required
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Upload Image</label>
            <input
              className="form-control"
              type="file"
              onChange={handleImageUpload}
              required={!isEditing}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Technical Area</label>
            <select
              className="form-control"
              value={repairMans.technicalArea.id || ""}
              onChange={handleTechnicalAreaChange}
              required
            >
              <option value="" disabled>
                Select Technical Area
              </option>
              {technicalAreas.map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name.az}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button className="btn btn-primary align-self-center px-5">
          {isEditing ? "Update" : "Submit"}
        </button>
      </form>
      <h5 className="text-center my-4">Repair Man List</h5>
      <div className="table-responsive">
        <table className="table table-hover m-0">
          <thead>
            <tr>
              <th scope="col" className="text-center" style={{ width: "60px" }}>
                №
              </th>
              <th scope="col">Name</th>
              <th scope="col">Surname</th>
              <th scope="col">Phone Number</th>
              <th scope="col">Technical Area (az)</th>
              <th scope="col">Technical Area (en)</th>
              <th scope="col">Technical Area (ru)</th>
              <th scope="col">Image</th>
              <th
                scope="col"
                className="text-center"
                style={{ width: "100px" }}
              >
                Edit
              </th>
              <th
                scope="col"
                className="text-center"
                style={{ width: "100px" }}
              >
                Delete
              </th>
            </tr>
          </thead>
          <tbody>
            {repairMan.map((type, index) => {
              // Use the getImageUrl function to handle the image path correctly
              const imageUrl = type.image 
                ? getImageUrl(type.image)
                : 'https://placehold.co/150x150/e9ecef/495057?text=No+Image';
              
              const technicalArea = type.technicalArea || {};
              const technicalAreaName = technicalArea.name || {};
              
              return (
                <tr key={type.id || type._id}>
                  <th scope="row" className="text-center">
                    {index + 1}
                  </th>
                  <td>{type.name}</td>
                  <td>{type.surname}</td>
                  <td>{type.phoneNumber}</td>
                  <td>{technicalAreaName.az || 'N/A'}</td>
                  <td>{technicalAreaName.en || 'N/A'}</td>
                  <td>{technicalAreaName.ru || 'N/A'}</td>
                  <td>
                    <img
                      src={imageUrl}
                      width={150}
                      alt={type.name || 'Repair Man'}
                      className="design-style-image"
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/150x150/e9ecef/495057?text=Error';
                        console.error('Image load error for:', type.image);
                      }}
                    />
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => handleEdit(type)}
                    >
                      <i className="bi bi-pencil-square"></i>
                      Edit
                    </button>
                  </td>
                  <td className="text-center">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(type._id || type.id)}
                    >
                      <i className="bi bi-trash3"></i>
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RepairManManager;
